/**
 * PublicCandidate — Bloco 3 (Fase A)
 *
 * Public page reachable at /candidato/:slug — no login required.
 *
 * Anyone with the link can:
 *   • see name / party / position / state
 *   • see the humanised verdict + nota 0-10
 *   • read the sources + consultation date
 *   • see the shareable card (image)
 *   • trigger the share bar (WhatsApp, X, download, copy)
 *   • start a free account via CTA "Ver análise completa"
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ShareBar } from "../components/ShareBar";
import { CandidateAvatar } from "../components/CandidateAvatar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const partyInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PARTY_HEX = {
  PT: "#CC0000", PSDB: "#0058A8", PL: "#003366", MDB: "#FAC828", PSOL: "#FFC72C",
  PSB: "#FF6600", REPUBLICANOS: "#008060", PODEMOS: "#00A8E0", PP: "#186CA8",
  PDT: "#CC3300", "UNIÃO": "#0066CC", NOVO: "#FFA500", "PCdoB": "#CC0000",
  REDE: "#008060", PV: "#00A860", AVANTE: "#FF8000", SOLIDARIEDADE: "#F08028",
  CIDADANIA: "#0060A8",
};
const partyColor = (party) => {
  if (!party) return "#64748B";
  const match = party.match(/\(([^)]+)\)/);
  const acro = (match?.[1] || party).toUpperCase().trim();
  return PARTY_HEX[acro] || "#64748B";
};

const verdictBadgeClass = (code) => {
  if (code === "CLEAN") return "bg-[#006030] text-white";
  if (code === "PENDING") return "bg-[#DC2626] text-white";
  return "bg-slate-400 text-white";
};

const verdictSymbol = (code) => {
  if (code === "CLEAN") return "✓";
  if (code === "PENDING") return "!";
  return "?";
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
};

function FollowCandidateBox({ slug, nome }) {
  const [email, setEmail] = useState(() => localStorage.getItem("todeolho:alerts-email") || "");
  const [submitting, setSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);

  const seguir = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Coloque um e-mail válido."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/candidatos/${slug}/seguir`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "erro");
      setFollowing(true);
      localStorage.setItem("todeolho:alerts-email", email);
      toast.success(j.message || "Prontinho!");
    } catch (err) {
      toast.error(err.message || "Não foi possível registrar agora.");
    } finally {
      setSubmitting(false);
    }
  };

  if (following) {
    return (
      <section className="bg-[#E8F5E9] border border-[#006030]/20 rounded-2xl p-5 shadow-sm mb-6" data-testid="public-candidate-following">
        <p className="font-semibold text-[#006030]">Você está acompanhando {nome}.</p>
        <p className="text-sm text-slate-700 mt-1">Vamos te avisar por e-mail quando houver novidade.</p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-2xl p-6 shadow-md mb-6" data-testid="public-candidate-follow">
      <p className="text-sm uppercase tracking-wide text-slate-500 mb-2 font-semibold">Quer receber avisos sobre {nome}?</p>
      <p className="text-xs text-slate-500 mb-3">Deixe seu e-mail e te avisamos quando algo importante mudar.</p>
      <form onSubmit={seguir} className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          className="flex-1 px-4 py-3 border border-slate-300 rounded-lg min-h-[44px]"
          data-testid="follow-email-input"
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-[#F59E0B] hover:bg-[#D97706] disabled:bg-slate-400 text-white font-semibold px-5 py-3 rounded-lg min-h-[44px]"
          data-testid="follow-submit-btn"
        >
          {submitting ? "Registrando…" : "Acompanhar"}
        </button>
      </form>
    </section>
  );
}


export default function PublicCandidate() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND}/api/public/candidatos/${slug}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "not_found" : "network");
        return r.json();
      })
      .then((json) => { if (!cancelled) setData(json); })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center px-4">
        <div className="text-center" data-testid="public-candidate-loading">
          <div className="inline-block w-10 h-10 border-4 border-slate-300 border-t-[#006030] rounded-full animate-spin mb-4"></div>
          <p className="text-slate-600">Consultando dados oficiais…</p>
        </div>
      </div>
    );
  }

  if (err === "not_found" || !data) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white rounded-2xl p-8 shadow-md" data-testid="public-candidate-not-found">
          <div className="text-5xl mb-3">🔍</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Não encontramos esse político</h1>
          <p className="text-slate-600 mb-6">
            O link pode estar quebrado ou o candidato ainda não foi analisado.
          </p>
          <Link
            to="/"
            className="inline-block bg-[#006030] hover:bg-[#004a24] text-white font-semibold px-6 py-3 rounded-lg min-h-[44px] transition-colors"
            data-testid="public-candidate-home-cta"
          >
            Consultar outro político
          </Link>
        </div>
      </div>
    );
  }

  const partySigla = (data.current_party || "").match(/\(([^)]+)\)/)?.[1] || data.current_party || "";
  const bgColor = partyColor(data.current_party);
  const consultedAt = formatDate(data.consulted_at);
  const site = BACKEND;
  const canonical = `${site}/candidato/${data.slug}`;
  const cardUrl = `${site}/api/public/candidatos/${data.slug}/card.png`;

  return (
    <div className="min-h-screen bg-[#F4F6F8]" data-testid="public-candidate-page">
      {/* Dynamic OG tags for browser tab + client-side reads (crawlers use /api/share/candidato/{slug}) */}
      <Helmet>
        <title>{`${data.name} | Tô de Olho`}</title>
        <meta name="description" content={`${data.verdict?.titulo || ""} · Nota ${data.integrity_score}/10`} />
        <meta property="og:title" content={`${data.name} | Tô de Olho`} />
        <meta property="og:description" content={`${data.verdict?.titulo || ""} · Nota ${data.integrity_score}/10`} />
        <meta property="og:image" content={cardUrl} />
        <meta property="og:image:width" content="1080" />
        <meta property="og:image:height" content="1080" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Tô de Olho" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={cardUrl} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Sticky brand bar */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand-home">
            <span className="text-2xl">🔍</span>
            <span className="font-bold text-slate-800">Tô de Olho</span>
          </Link>
          <Link
            to="/auth"
            className="text-sm text-[#006030] font-semibold hover:underline"
            data-testid="public-header-signup-cta"
          >
            Entrar / Criar conta
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-24" style={{ paddingBottom: "max(6rem, env(safe-area-inset-bottom))" }}>
        {/* Candidate header */}
        <section className="bg-white rounded-2xl p-6 shadow-md mb-6" data-testid="public-candidate-header">
          <div className="flex items-start gap-4">
            <CandidateAvatar slug={data.slug} name={data.name} party={data.current_party} size={80} />
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight" data-testid="public-candidate-name">
                {data.name}
              </h1>
              <p className="text-slate-600 mt-1">
                {[partySigla, data.position, data.state].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        </section>

        {/* Verdict + score */}
        <section className="bg-white rounded-2xl p-6 shadow-md mb-6" data-testid="public-candidate-verdict">
          <p className="text-sm uppercase tracking-wide text-slate-500 mb-3 font-semibold">
            O que encontramos sobre esse político
          </p>
          <div className="flex items-center gap-3 mb-3">
            <span className={`inline-flex w-8 h-8 items-center justify-center rounded-full text-lg font-bold ${verdictBadgeClass(data.verdict?.code)}`}>
              {verdictSymbol(data.verdict?.code)}
            </span>
            <h2 className="text-xl font-bold text-slate-800">{data.verdict?.titulo}</h2>
          </div>
          <p className="text-slate-700 leading-relaxed mb-4">{data.verdict?.resumo}</p>

          <div className="bg-[#1E3A5F] rounded-xl p-4 flex items-center justify-between">
            <span className="text-white text-sm font-medium uppercase tracking-wide">Nota de integridade</span>
            <span className="text-white text-3xl font-bold" data-testid="public-candidate-score">
              {data.integrity_score}<span className="text-lg text-slate-300">/10</span>
            </span>
          </div>
        </section>

        {/* Share bar */}
        <section className="bg-white rounded-2xl p-6 shadow-md mb-6" data-testid="public-candidate-share">
          <p className="text-sm uppercase tracking-wide text-slate-500 mb-4 font-semibold">
            Compartilhe com quem importa
          </p>
          <ShareBar candidate={data} />
        </section>

        <FollowCandidateBox slug={data.slug} nome={data.name} />

        {/* Preview do card */}
        <section className="bg-white rounded-2xl p-6 shadow-md mb-6">
          <p className="text-sm uppercase tracking-wide text-slate-500 mb-3 font-semibold">Prévia do card</p>
          <img
            src={cardUrl}
            alt={`Card compartilhável de ${data.name}`}
            className="w-full rounded-lg border border-slate-200"
            data-testid="public-candidate-card-preview"
            loading="lazy"
          />
        </section>

        {/* Sources */}
        <section className="bg-white rounded-2xl p-6 shadow-md mb-6" data-testid="public-candidate-sources">
          <p className="text-sm uppercase tracking-wide text-slate-500 mb-3 font-semibold">Fontes consultadas</p>
          <ul className="space-y-2">
            {(data.sources || []).map((s, i) => (
              <li key={i} className="text-slate-700 text-sm flex gap-2">
                <span className="text-slate-400">•</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
          {consultedAt && (
            <p className="text-xs text-slate-500 mt-4">Consultado em {consultedAt}</p>
          )}
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-[#006030] to-[#004a24] rounded-2xl p-6 text-center shadow-md mb-6">
          <h3 className="text-white text-xl font-bold mb-2">Quer ver tudo que descobrimos?</h3>
          <p className="text-green-100 text-sm mb-4">
            Crie uma conta gratuita e acesse a análise completa, sem cartão.
          </p>
          <Link
            to="/auth"
            className="inline-block bg-white text-[#006030] font-bold px-8 py-3 rounded-lg min-h-[44px] hover:bg-slate-100 transition-colors"
            data-testid="public-candidate-signup-cta"
          >
            Começar grátis — sem cartão
          </Link>
        </section>

        {/* Legal disclaimer (humanised — Bloco 5) */}
        <section className="text-xs text-slate-500 leading-relaxed px-2" data-testid="public-candidate-disclaimer">
          {data.disclaimer}
        </section>
      </main>
    </div>
  );
}
